import { memo } from "react";

import { Transition } from "@headlessui/react";

import { Icon } from "@components/Icon";
import { RichText } from "@components/RichText";
import { Text } from "@components/Text";
import { type AccordionItemBlock } from "@react/components/Accordion/Accordion.view";
import {
  STOREFRONT_TRANSITION_DURATION,
  STOREFRONT_TRANSITION_EASING,
} from "@root/tablescape/constants";
import { type TTailwindString, tw } from "classnames";

const styles = {
  container: tw(
    "w-full",
    "table",
    "table-fixed",
    "border-0",
    "border-solid",
    "border-b",
    "border-divider",
    "text-background-primary-inverse"
  ),
  accordionButton: tw(
    "w-full",
    "flex",
    "items-center",
    "justify-between",
    "px-0",
    "py-6",
    "h-[60px]" as TTailwindString,
    "cursor-pointer",
    "border-0",
    "bg-transparent",
    "hover:no-underline"
  ),
  buttonText: tw("flex-1", "text-left", "text-background-primary-inverse"),
  icon: tw("w-7", "h-7", "text-background-primary-inverse"),
  transition: {
    className: tw(
      "grid",
      "transition-all",
      STOREFRONT_TRANSITION_DURATION,
      STOREFRONT_TRANSITION_EASING,
      "pb-6"
    ),
    enterFrom: tw("grid-rows-collapse", "opacity-0", "invisible"),
    enterTo: tw("grid-rows-expand", "opacity-100", "visible", "pb-6"),
    leaveFrom: tw("grid-rows-expand", "opacity-100"),
    leaveTo: tw("grid-rows-collapse", "opacity-0", "overflow-hidden"),
  },
  content: tw("min-h-0", "text-background-primary-inverse"),
};

type Props = {
  className?: TTailwindString | undefined;
  content: AccordionItemBlock["content"];
  heading: string;
  isOpen: boolean;
  onClick: () => void;
};

const AccordionItemViewBase = ({
  className,
  content,
  heading,
  isOpen,
  onClick,
}: Props) => {
  const resolvedContainerStyles = tw(className, styles.container);

  return (
    <>
      <div className={resolvedContainerStyles}>
        <button className={styles.accordionButton} onClick={onClick}>
          <Text as="title2" className={styles.buttonText}>
            {heading}
          </Text>
          <Icon className={styles.icon} name={isOpen ? "minus" : "plus"} />
        </button>
        <Transition show={isOpen} {...styles.transition}>
          <div className={styles.content}>
            {typeof content === "string" ? (
              <RichText content={content} />
            ) : (
              content
            )}
          </div>
        </Transition>
      </div>
    </>
  );
};

export const AccordionItemView = memo(AccordionItemViewBase);
